import * as React from "react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

/**
 * The one dropdown used everywhere in NUMA.
 *
 * A native `<select>` is rendered by the operating system: it ignores the
 * palette, the radius and the dark theme, so it reads as a foreign object on
 * the page - and on Windows it still draws the grey 1995 arrow. This wraps the
 * Radix listbox behind the same short call signature the native element had,
 * so a call site is one element rather than six, and every menu in the product
 * looks and behaves the same way.
 *
 * `options` is `[{ value, label, hint }]`; `hint` is the dimmer trailing note
 * that would otherwise have to be glued into the label.
 */
export default function NumaSelect({
  value,
  onValueChange,
  options = [],
  id,
  testId,
  className,
  contentClassName,
  placeholder,
  title,
  disabled,
  onPointerDown,
  "aria-label": ariaLabel,
}) {
  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger
        id={id}
        data-testid={testId}
        aria-label={ariaLabel}
        title={title}
        // Opening the menu moves focus away from whatever had it; call sites
        // that depend on a live text selection (the editor toolbar) save it here.
        onPointerDown={onPointerDown}
        className={cn("w-auto", className)}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className={contentClassName}>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
            {option.hint && (
              <span className="ml-1.5 text-ink-subtle">{option.hint}</span>
            )}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
